import type {ReifiedMeshData} from "#template/interfaces/mesh-data"
import type {ControlPoint, CurvePoints} from "#template/interfaces/object-data"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {PositionControlPoint as PositionControlPointData, UVControlPoint as UVControlPointData} from "#template/nodes/object/curve/types"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import {ParametricCurve} from "#template/utils/parametric-curve"
import {Three as THREE} from "@cm/material-nodes/three"
import {Vector2, Vector3} from "@cm/math"

const TD = TypeDescriptors

const sampleCurveDescriptor = {
    closed: TD.inlet(TD.Primitive<boolean | "repeat">()),
    allowScaling: TD.inlet(TD.Primitive<boolean>()),
    controlPoints: TD.inlet(TD.Identity<ControlPoint[]>()),
    segmentLength: TD.inlet(TD.Primitive<number>()),
    offsetLength: TD.inlet(TD.Primitive<number | undefined>()),
    curvePoints: TD.outlet(
        TD.Nullable(
            TD.Identity<{
                points: Float32Array
                normals: Float32Array
                tangents: Float32Array
                segments: Float32Array
                scales: Float32Array
                sectionIds: Uint32Array
            }>(),
        ),
    ),
}

function toThreeVector(vector: Vector3): THREE.Vector3 {
    return new THREE.Vector3(vector.x, vector.y, vector.z)
}

class IndexedCurvePath extends THREE.CurvePath<THREE.Vector3> {
    constructor() {
        super()
    }

    private getIndexedVector(t: number, type: "position" | "tangent", optionalTarget?: THREE.Vector3) {
        const d = t * this.getLength()
        const curveLengths = this.getCurveLengths()
        let i = 0

        while (i < curveLengths.length) {
            if (curveLengths[i] >= d) {
                const diff = curveLengths[i] - d
                const curve = this.curves[i]
                const segmentLength = curve.getLength()
                const u = segmentLength === 0 ? 0 : 1 - diff / segmentLength
                const t = curve.getUtoTmapping(u, undefined as unknown as number)

                return {vector: type === "position" ? curve.getPoint(t, optionalTarget) : curve.getTangent(t, optionalTarget), curveId: i, t}
            }

            i++
        }

        throw new Error("Invalid t value")
    }

    getSpacedIndexedVector(divisions: number, type: "position" | "tangent") {
        const points: {
            vector: THREE.Vector3
            curveId: number
            t: number
        }[] = []

        for (let i = 0; i <= divisions; i++) {
            points.push(this.getIndexedVector(i / divisions, type))
        }

        if (this.autoClose && points.length > 0) points.push(points[0])

        return points
    }

    sampleIndexedVector(tSamples: number[], type: "position" | "tangent") {
        const points = tSamples.map((t) => this.getIndexedVector(t, type))

        if (this.autoClose && points.length > 0) points.push(points[0])

        return points
    }
}

export function getSectionIdForControlPointIdxMap(controlPoints: {readonly sectionBreak?: boolean}[], closed: boolean): Record<number, number> {
    const indexedControlPoints = controlPoints.map((controlPoint, index) => ({...controlPoint, index}))
    const sectionIdForControlPointIdx: Record<number, number> = {}

    const firstSectionBreak = indexedControlPoints.find((controlPoint) => controlPoint.sectionBreak)
    if (!firstSectionBreak) {
        indexedControlPoints.forEach((controlPoint) => {
            sectionIdForControlPointIdx[controlPoint.index] = 0
        })
        return sectionIdForControlPointIdx
    }

    const reshuffledControlPoints = closed
        ? indexedControlPoints.slice(firstSectionBreak.index).concat(indexedControlPoints.slice(0, firstSectionBreak.index))
        : indexedControlPoints

    let currentSectionId = 0
    reshuffledControlPoints.forEach((controlPoint, index) => {
        if (controlPoint.sectionBreak && index !== 0) currentSectionId++
        sectionIdForControlPointIdx[controlPoint.index] = currentSectionId
    })
    return sectionIdForControlPointIdx
}

export class SampleCurve implements NodeClassImpl<typeof sampleCurveDescriptor, typeof SampleCurve> {
    static descriptor = sampleCurveDescriptor
    static uniqueName = "SampleCurve"
    closed!: Inlet<boolean | "repeat">
    allowScaling!: Inlet<boolean>
    controlPoints!: Inlet<ControlPoint[]>
    segmentLength!: Inlet<number>
    offsetLength!: Inlet<number | undefined>
    curvePoints!: Outlet<CurvePoints | null>

    run() {
        if (
            this.closed === NotReady ||
            this.allowScaling === NotReady ||
            this.controlPoints === NotReady ||
            this.segmentLength === NotReady ||
            this.offsetLength === NotReady
        ) {
            this.curvePoints.emitIfChanged(NotReady)
            return
        }

        if (this.segmentLength <= 0) {
            this.curvePoints.emitIfChanged(null)
            return
        }

        const closed = this.closed === "repeat" || this.closed

        const curvePath = new IndexedCurvePath()

        const indexedControlPoints = this.controlPoints.map((controlPoint, index) => ({...controlPoint, index}))
        const indexedControlPointsPerCurve: (typeof indexedControlPoints)[] = []

        if (indexedControlPoints.length >= 2) {
            const firstCornerIndex = indexedControlPoints.findIndex((point) => point.corner)

            if (firstCornerIndex !== -1) {
                const reshuffledControlPoints = closed
                    ? indexedControlPoints.slice(firstCornerIndex).concat(indexedControlPoints.slice(0, firstCornerIndex))
                    : indexedControlPoints

                let currentCurvePoints: typeof indexedControlPoints = []
                for (let i = 0; i < reshuffledControlPoints.length; i++) {
                    const currentCurvePoint = reshuffledControlPoints[i]
                    const {corner} = currentCurvePoint

                    currentCurvePoints.push(currentCurvePoint)

                    if ((corner && i !== 0) || i === reshuffledControlPoints.length - 1) {
                        if (i === reshuffledControlPoints.length - 1 && closed) {
                            if (currentCurvePoints.length >= 2) if (!corner) currentCurvePoints.push(reshuffledControlPoints[0])
                        }
                        if (currentCurvePoints.length >= 2) {
                            const curve = new ParametricCurve(
                                currentCurvePoints.map(({position}) => toThreeVector(position)),
                                false,
                                "centripetal",
                            )
                            curvePath.add(curve)
                            indexedControlPointsPerCurve.push(currentCurvePoints)
                        }
                        currentCurvePoints = [currentCurvePoint]
                    }
                }

                if (closed && currentCurvePoints.length === 1) {
                    const currentCurvePoint = currentCurvePoints[0]
                    const {corner} = currentCurvePoint

                    if (corner) {
                        currentCurvePoints.push(reshuffledControlPoints[0])
                        const curve = new ParametricCurve(
                            currentCurvePoints.map(({position}) => toThreeVector(position)),
                            false,
                            "centripetal",
                        )
                        curvePath.add(curve)
                        indexedControlPointsPerCurve.push(currentCurvePoints)
                    }
                }
            } else {
                const curve = new ParametricCurve(
                    indexedControlPoints.map(({position}) => toThreeVector(position)),
                    closed && indexedControlPoints.length > 2,
                    "centripetal",
                )
                curvePath.add(curve)
                indexedControlPointsPerCurve.push(indexedControlPoints)
            }
        }

        if (curvePath.curves.length > 0) {
            curvePath.autoClose = this.closed === "repeat"

            const segmentLength = this.segmentLength
            const offsetLength = this.offsetLength ?? 0
            const allowScaling = this.allowScaling

            const accumulatedCurveLengths = curvePath.getCurveLengths()
            const totalCurveLength = curvePath.getLength()

            const tSamples: number[] = []
            const scales: number[] = []
            curvePath.curves.forEach((curve, i) => {
                const currentCurveLength = curve.getLength()
                if (currentCurveLength > segmentLength || allowScaling) {
                    const curveLengthOffset = i > 0 ? accumulatedCurveLengths[i - 1] : 0
                    const numSamples = allowScaling
                        ? Math.max(Math.round(currentCurveLength / segmentLength), 1)
                        : Math.max(Math.floor(currentCurveLength / segmentLength), 0)
                    const unusedSpace = currentCurveLength - numSamples * segmentLength
                    const addedStepSize = unusedSpace / (numSamples + 1)
                    const stepSize = segmentLength + addedStepSize
                    const currentScale = allowScaling ? currentCurveLength / (numSamples * segmentLength) : 1.0

                    for (let j = 0; j < numSamples; j++) {
                        const localT = offsetLength + segmentLength / 2 + addedStepSize + j * stepSize
                        if (localT < 0 || localT > currentCurveLength - offsetLength) continue
                        const t = (curveLengthOffset + localT) / totalCurveLength
                        tSamples.push(t)
                        scales.push(currentScale)
                    }
                }
            })

            const points = curvePath.sampleIndexedVector(tSamples, "position")

            if (curvePath.autoClose && scales.length > 0) scales.push(scales[0])

            const normals = points.map(({curveId, t}) => {
                const indexedControlPoints = indexedControlPointsPerCurve[curveId]
                const {closed} = curvePath.curves[curveId] as ParametricCurve

                const index = t * (indexedControlPoints.length - (closed ? 0 : 1))

                const start = indexedControlPoints[Math.floor(index)]
                const end = indexedControlPoints[Math.ceil(index) % indexedControlPoints.length]

                const localT = index - Math.floor(index)

                const quatStart = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), start.normal)
                const quatEnd = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), end.normal)

                const interpolatedQuat = quatStart.slerp(quatEnd, localT)

                return new THREE.Vector3(1, 0, 0).applyQuaternion(interpolatedQuat).normalize()
            })

            const tangents = curvePath.sampleIndexedVector(tSamples, "tangent")

            const segments = points.map(({curveId, t}) => {
                const indexedControlPoints = indexedControlPointsPerCurve[curveId]
                const {closed} = curvePath.curves[curveId] as ParametricCurve

                const index = t * (indexedControlPoints.length - (closed ? 0 : 1))

                const start = indexedControlPoints[Math.floor(index)]

                const localT = index - Math.floor(index)

                return start.index * (1 - localT) + (start.index + 1) * localT
            })

            const sectionIdForControlPointIdx = getSectionIdForControlPointIdxMap(indexedControlPoints, closed)
            const sectionIds = segments.map((segment) => {
                const controlPointIdx = Math.floor(segment)
                const sectionId = sectionIdForControlPointIdx[controlPointIdx]
                if (sectionId === undefined) throw new Error("Invalid section ID")
                return sectionId
            })

            this.curvePoints.emitIfChanged({
                points: new Float32Array(points.flatMap(({vector}) => [vector.x, vector.y, vector.z])),
                normals: new Float32Array(normals.flatMap((normal) => [normal.x, normal.y, normal.z])),
                tangents: new Float32Array(tangents.flatMap(({vector}) => [vector.x, vector.y, vector.z])),
                segments: new Float32Array(segments),
                scales: new Float32Array(scales),
                sectionIds: new Uint32Array(sectionIds),
            })
        } else {
            this.curvePoints.emitIfChanged(null)
        }
    }
}

const getControlPointsDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    controlPointsData: TD.inlet<PositionControlPointData[] | UVControlPointData[]>(TD.JSON()),
    reifiedMeshData: TD.inlet<ReifiedMeshData>(TD.ReifiedMeshData),
    uvChannel: TD.inlet<null | number>(TD.Nullable(TD.Number)),
    controlPoints: TD.outlet<ControlPoint[]>(TD.Identity()),
}

export class GetControlPoints implements NodeClassImpl<typeof getControlPointsDescriptor, typeof GetControlPoints> {
    static descriptor = getControlPointsDescriptor
    static uniqueName = "GetControlPoints"
    sceneManager!: Inlet<ISceneManager>
    controlPointsData!: Inlet<PositionControlPointData[] | UVControlPointData[]>
    reifiedMeshData!: Inlet<ReifiedMeshData>
    uvChannel!: Inlet<null | number>
    controlPoints!: Outlet<ControlPoint[]>

    run() {
        if (this.sceneManager === NotReady || this.controlPointsData === NotReady || this.reifiedMeshData === NotReady || this.uvChannel === NotReady) {
            this.controlPoints.emitIfChanged(NotReady)
            return
        }
        this.controlPoints.emitIfChanged(this.resolveControlPoints(this.controlPointsData))
    }

    private resolveControlPoints(controlPointsData: PositionControlPointData[] | UVControlPointData[]): ControlPoint[] {
        if (this.sceneManager === NotReady || this.reifiedMeshData === NotReady || this.uvChannel === NotReady) return []
        const transformedControlPoints: ControlPoint[] = []
        if (controlPointsData.length === 0) return transformedControlPoints

        const sceneManager = this.sceneManager
        const reifiedMeshData = this.reifiedMeshData
        const uvChannel = this.uvChannel

        const usingUVControlPoints = (x: PositionControlPointData[] | UVControlPointData[]): x is UVControlPointData[] => x[0].hasOwnProperty("uv")
        if (usingUVControlPoints(controlPointsData)) {
            if (uvChannel === null) {
                throw new Error("UV channel must be set if using UV control points")
            }

            controlPointsData.forEach((controlPoint) => {
                const controlPointUV = new Vector2(controlPoint.uv[0], controlPoint.uv[1])
                const intersection = sceneManager.getMeshGeometryQueries().projectPointToMeshInUVSpace(reifiedMeshData, uvChannel, controlPointUV)
                if (intersection === null) throw new Error("Failed to convert control point from UV to position, no intersection found")
                transformedControlPoints.push({
                    position: intersection.position,
                    normal: intersection.normal,
                    corner: controlPoint.corner,
                    sectionBreak: controlPoint.sectionBreak ?? false,
                })
            })
        } else {
            controlPointsData.forEach((pointData) =>
                transformedControlPoints.push({
                    position: Vector3.fromArray(pointData.position),
                    normal: Vector3.fromArray(pointData.normal),
                    corner: pointData.corner,
                    sectionBreak: pointData.sectionBreak ?? false,
                }),
            )
        }

        return transformedControlPoints
    }
}
